/**
 * Countdown de promo, version tira fina. Sin dependencias.
 * Cada digito hace un micro-tick (sube y aparece) solo cuando cambia.
 */

const PAD = (n, len) => String(Math.max(0, n)).padStart(len, '0');

class PromoCountdown extends HTMLElement {
  connectedCallback() {
    this.deadline = Date.parse(this.dataset.deadline);
    if (Number.isNaN(this.deadline)) {
      this.hidden = true;
      return;
    }

    this.urgentMs = (parseFloat(this.dataset.urgentHours) || 24) * 3600000;
    this.hideOnEnd = this.dataset.hideOnEnd === 'true';
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.units = new Map();
    for (const el of this.querySelectorAll('[data-unit]')) {
      this.units.set(el.dataset.unit, {
        root: el,
        digits: [...el.querySelectorAll('[data-digit]')],
      });
    }
    this.live = this.querySelector('[data-live]');
    this.lastLive = '';

    this.tick();
    this.start();

    this.onVisibility = () => (document.hidden ? this.stop() : (this.tick(), this.start()));
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  start() {
    this.stop();
    // Alineado al siguiente segundo real: nada de acumular deriva.
    this.timer = setTimeout(() => {
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }, 1000 - (Date.now() % 1000));
  }

  stop() {
    clearTimeout(this.timer);
    clearInterval(this.timer);
  }

  tick() {
    const remaining = this.deadline - Date.now();

    if (remaining <= 0) {
      this.stop();
      this.classList.add('is-over');
      if (this.hideOnEnd) this.hidden = true;
      this.setAll({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    const total = Math.floor(remaining / 1000);
    const parts = {
      days: Math.floor(total / 86400),
      hours: Math.floor(total / 3600) % 24,
      minutes: Math.floor(total / 60) % 60,
      seconds: total % 60,
    };

    // Ultimo dia: fuera el bloque de dias, el label pasa a "ultimo dia".
    const urgent = remaining <= this.urgentMs;
    this.classList.toggle('is-urgent', urgent);
    const daysUnit = this.units.get('days');
    if (daysUnit) daysUnit.root.hidden = urgent;

    this.setAll(parts);
    this.announce(parts, urgent);
  }

  setAll(parts) {
    for (const [name, unit] of this.units) {
      const value = PAD(parts[name], unit.digits.length);
      unit.digits.forEach((digit, i) => this.setDigit(digit, value[i]));
    }
  }

  setDigit(digit, char) {
    if (digit.textContent === char) return;
    digit.textContent = char;
    if (this.reduced) return;
    digit.classList.remove('is-ticking');
    void digit.offsetWidth; // reinicia la animacion si el digito encadena cambios
    digit.classList.add('is-ticking');
  }

  /** Lectores de pantalla: una vez por minuto, no 60 veces. */
  announce(parts, urgent) {
    if (!this.live) return;
    const template = urgent ? this.live.dataset.templateUrgent : this.live.dataset.template;
    if (!template) return;
    // Placeholders en corchetes: {{ }} lo interpolaria Liquid antes de llegar aqui.
    const text = template
      .replace('[days]', parts.days)
      .replace('[hours]', parts.hours)
      .replace('[minutes]', parts.minutes);
    if (text !== this.lastLive) {
      this.live.textContent = text;
      this.lastLive = text;
    }
  }
}

if (!customElements.get('promo-countdown')) {
  customElements.define('promo-countdown', PromoCountdown);
}
